/**
 * PA system report logic, ported from the AtlasIED System Report desktop app
 * (renderer.js CSV handling + report_analyzer.py). Pure functions over
 * PapaParse row arrays, so it runs client-side with no Python.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const norm = (v) => String(v ?? '').trim().replace(/\s+/g, ' ');
const normHeader = (v) => norm(v).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
const plural = (n, s, p) => `${n} ${n === 1 ? s : p || `${s}s`}`;
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);
export const fmtPct = (v) => {
  const f = clamp(v, 0, 100).toFixed(1);
  return `${f.endsWith('.0') ? f.slice(0, -2) : f}%`;
};
const isCompleted = (r) => /completed/i.test(r.status || '');

export function parseDate(value) {
  const t = norm(value);
  if (!t) return null;
  let m = t.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2}|\d{4})$/);
  if (m) {
    const mi = MONTHS.findIndex((n) => n.slice(0, 3).toLowerCase() === m[2].toLowerCase());
    const y = Number(m[3]);
    return mi < 0 ? null : new Date(y < 100 ? 2000 + y : y, mi, Number(m[1]));
  }
  m = t.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    // Day-first like the Python analyzer, falling back to month-first when that can't be a date.
    const [a, b] = [Number(m[1]), Number(m[2])];
    return b <= 12 ? new Date(Number(m[3]), b - 1, a) : new Date(Number(m[3]), a - 1, b);
  }
  const d = Date.parse(t);
  return Number.isNaN(d) ? null : new Date(d);
}

/* ---- CSV rows -> records ---- */

/** PM / CM exports: finds the header row (exports carry a preamble) and maps rows to objects. */
export function toRecords(rows) {
  const hi = rows.findIndex((r) => {
    const h = r.map(normHeader);
    return (h.includes('work_order') && h.includes('scheduled_date')) || (h.includes('wo_no') && h.includes('fault_description'));
  });
  if (hi < 0) throw new Error('Header row not found in CSV.');
  const headers = rows[hi].map(normHeader);
  return rows.slice(hi + 1).filter((r) => r.some((c) => norm(c))).map((r) => {
    const o = {};
    headers.forEach((h, i) => { if (h) o[h] = norm(r[i]); });
    return o;
  });
}

export function detectType(records) {
  const s = records[0] || {};
  if ('scheduled_date' in s || 'activity_code' in s) return 'pm';
  if ('wo_no' in s || 'fault_description' in s) return 'cm';
  return 'unknown';
}

export function cmItemsToRecords(rows) {
  const toHeader = (v) => normHeader(v).replace(/^wo_no_?$/, 'wo_no');
  const findIdx = (headers, names) => headers.findIndex((h) => names.includes(h));
  const DESC = ['description', 'item', 'material', 'material_description'];
  const QTY = ['issued', 'qty', 'quantity'];

  const hi = rows.findIndex((r) => {
    const h = r.map(toHeader);
    return findIdx(h, DESC) >= 0 || (findIdx(h, ['wo_no']) >= 0 && findIdx(h, ['material_details']) >= 0 && findIdx(h, QTY) >= 0);
  });
  if (hi < 0) throw new Error("This file doesn't look like a CM Items export.");

  const headers = rows[hi].map(toHeader);
  const woIdx = findIdx(headers, ['wo_no']);
  const codeIdx = findIdx(headers, ['material_details', 'material_code', 'part_no', 'part_number']);
  const qtyIdx = findIdx(headers, QTY);
  let descIdx = findIdx(headers, DESC);
  // Some exports leave the description column (right after WO No) unnamed.
  if (descIdx < 0 && woIdx >= 0 && woIdx + 1 < headers.length && !headers[woIdx + 1]) descIdx = woIdx + 1;

  return rows.slice(hi + 1).filter((r) => r.some((c) => norm(c))).map((r) => {
    const o = {};
    headers.forEach((h, i) => { if (h) o[h] = norm(r[i]); });
    if (descIdx >= 0) o.description = norm(r[descIdx]);
    if (codeIdx >= 0) o.material_code = norm(r[codeIdx]);
    if (qtyIdx >= 0) o.qty = norm(r[qtyIdx]);
    return o;
  });
}

export function aggregateCmItems(records) {
  const map = new Map();
  records.forEach((r) => {
    const desc = norm(r.description || r.item || r.material || r.material_code || r.material_details || r.part_no).toUpperCase();
    if (!desc) return;
    const parts = norm(r.issued || r.qty || r.quantity || '1').match(/\d+/g);
    map.set(desc, (map.get(desc) || 0) + (parts ? parts.map(Number).reduce((a, b) => a + b, 0) : 1));
  });
  const sorted = [...map.entries()].sort((a, b) => b[1] - a[1]);
  return { labels: sorted.map((e) => e[0]), values: sorted.map((e) => e[1]) };
}

/* ---- analysis (report_analyzer.py) ---- */

function extractZone(value) {
  const t = norm(value).toUpperCase().replace(/C-/g, 'C');
  if (!t) return '';
  const m = t.match(/\b([A-Z]\d{1,2})\b/) || t.match(/\b([A-Z]{2})-/) || t.match(/\b([A-Z]{2})\b/);
  return m ? m[1] : t.split(' ')[0];
}

const extractRack = (code) => norm(code).toUpperCase().match(/RACK-\d+/)?.[0] || 'Other';

function classifySeverity(desc, remarks) {
  const t = `${norm(desc)} ${norm(remarks)}`.toLowerCase();
  if (/(not working|faulty|error|controller|line card|mic station|communication|open cable|cable)/.test(t)) return 'errors';
  if (/(beep|cracking|loud|detached|refix|align|dirty|rusty|cover)/.test(t)) return 'warnings';
  return 'alerts';
}

function categoriseFault(desc) {
  const d = norm(desc).toUpperCase();
  const any = (keys) => keys.some((k) => d.includes(k));
  if (!d) return 'Other';
  if (any(['COVER', 'DETACH', 'MISALIGN', 'RESTED', 'DIRTY', 'RUSTY', 'ALIGNED'])) return 'Speaker Cover / Physical';
  if (any(['FAN', 'COOLING', 'NOISY', 'LINE CARD'])) return 'Amplifier / Fan';
  if (d.includes('CONTROLLER')) return 'Controller Error';
  if (any(['NOT WORKING', 'FAULTY', 'VOLUME', 'PA SYSTEM', 'ANNOUNCEMENT'])) return 'System / PA Fault';
  if (any(['CABLE', 'OPEN', 'PATCH'])) return 'Cable / Wiring';
  return 'Other';
}

function classifyCorrectiveAction(remarks) {
  const t = norm(remarks).toLowerCase();
  if (!t) return 'Inspection';
  if (/cancelled|linc/.test(t)) return 'Cancelled / LINC';
  if (t.includes('replace')) return 'Replacement';
  if (/reinitial|reset/.test(t)) return 'Reset / Reinit';
  if (/cable|patch|open/.test(t)) return 'Cable Repair';
  if (/refix|align|adjust|detached/.test(t)) return 'Realignment';
  if (/check|inspect|found/.test(t)) return 'Inspection';
  return 'General Fix';
}

/** Counter.most_common equivalent: [label, count] sorted desc, ties keep first-seen order. */
function mostCommon(records, selector, limit) {
  const counts = new Map();
  records.forEach((r) => { const k = selector(r); if (k) counts.set(k, (counts.get(k) || 0) + 1); });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit);
}
const series = (entries) => ({ labels: entries.map((e) => e[0]), values: entries.map((e) => e[1]) });

const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const pmDate = (r) => parseDate(r.scheduled_date || r.date);
const cmDate = (r) => parseDate(r.received_date || r.date);

function dateRange(dates) {
  if (!dates.length) return '';
  const s = [...dates].sort((a, b) => a - b);
  const [f, l] = [s[0], s[s.length - 1]];
  const mon = (d) => MONTHS[d.getMonth()].slice(0, 3);
  return f.getMonth() === l.getMonth() && f.getFullYear() === l.getFullYear()
    ? `${f.getDate()}-${l.getDate()} ${mon(f)} ${f.getFullYear()}`
    : `${f.getDate()} ${mon(f)} - ${l.getDate()} ${mon(l)} ${l.getFullYear()}`;
}

function buildTimeline(pmRecords, cmRecords) {
  const perDay = new Map();
  const bump = (d, key) => {
    if (!d) return;
    const k = dayKey(d);
    if (!perDay.has(k)) perDay.set(k, { d, pm: 0, cm: 0 });
    perDay.get(k)[key] += 1;
  };
  pmRecords.forEach((r) => bump(pmDate(r), 'pm'));
  cmRecords.forEach((r) => bump(cmDate(r), 'cm'));
  const days = [...perDay.entries()].sort((a, b) => a[0].localeCompare(b[0])).map((e) => e[1]);
  return {
    labels: days.map(({ d }) => `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()].slice(0, 3)}`),
    pm: days.map((x) => x.pm),
    cm: days.map((x) => x.cm),
  };
}

/** Derives report fields and chart series from PM / CM records. Empty strings mean "no data, keep the current value". */
export function analyze({ pmRecords = [], cmRecords = [] }) {
  const pmDates = pmRecords.map(pmDate).filter(Boolean);
  const cmDates = cmRecords.map(cmDate).filter(Boolean);
  const zones = new Set([
    ...pmRecords.map((r) => extractZone(r.contractor_location || r.location)),
    ...cmRecords.map((r) => extractZone(r.location)),
  ].filter(Boolean));

  const sev = { errors: 0, warnings: 0, alerts: 0 };
  cmRecords.forEach((r) => { sev[classifySeverity(r.fault_description, r.remarks)] += 1; });
  const actions = mostCommon(cmRecords, (r) => classifyCorrectiveAction(r.remarks));
  const cm = { count: cmRecords.length, completed: cmRecords.filter(isCompleted).length };
  cm.pending = cm.count - cm.completed;
  const pm = { count: pmRecords.length, completed: pmRecords.filter(isCompleted).length };

  // Health heuristic, unchanged from the desktop app.
  const pressure = (cm.count + sev.errors * 0.9 + sev.warnings * 0.55 + sev.alerts * 0.65) / Math.max(pm.count, 12);
  let connectivity = 'Stable';
  let connectivityNote = 'No major interruptions reported.';
  if (cm.pending > 0 || sev.errors >= 6) {
    connectivity = 'Monitor';
    connectivityNote = `${plural(cm.pending || sev.errors, 'open item')} requires follow-up.`;
  } else if (cm.count) connectivityNote = `${plural(cm.completed, 'corrective ticket')} closed during this reporting cycle.`;
  else if (pm.count) connectivityNote = `${plural(pm.completed, 'preventive visit')} completed without logged network interruptions.`;

  const serviceDays = new Set(pmDates.map(dayKey)).size;
  const allDates = [...pmDates, ...cmDates];
  const themes = actions.slice(0, 2).map((e) => e[0]);
  const actionLabel = actions[0] ? actions[0][0].toLowerCase() : 'field follow-up';
  const hasCm = cm.count > 0;

  return {
    reportMonth: allDates[0] ? MONTHS[allDates[0].getMonth()] : '',
    activeZones: zones.size ? String(zones.size) : '',
    devicesOnline: fmtPct(clamp(100 - pressure * 8 - cm.pending * 1.5, 85, 100)),
    uptime: fmtPct(clamp(100 - pressure * 6 - cm.pending * 1.2, 88, 100)),
    connectivity,
    connectivityNote,
    errors: hasCm ? String(sev.errors) : '',
    warnings: hasCm ? String(sev.warnings) : '',
    alerts: hasCm ? String(sev.alerts) : '',
    preventiveChecks: pm.count
      ? `${plural(pm.count, 'check')} completed across ${plural(serviceDays, 'service day')}${pmDates.length ? ` (${dateRange(pmDates)})` : ''}.`
      : '',
    correctiveActions: hasCm
      ? `${plural(cm.completed, 'issue')} resolved with focus on ${themes.length ? themes.join(', ').toLowerCase() : 'documented corrective work'}.`
      : '',
    pendingTasks: hasCm ? (cm.pending ? `${plural(cm.pending, 'task')} pending corrective closure.` : 'None.') : pm.count ? 'None.' : '',
    rec1: cm.pending > 0
      ? `Close ${plural(cm.pending, 'open corrective item')} and confirm site readiness.`
      : 'Keep the corrective closure cycle inside the current monthly reporting window.',
    rec2: 'Sustain the preventive maintenance schedule across all planned service dates.',
    rec3: hasCm ? `Keep spares aligned with the current ${actionLabel} pattern.` : 'Keep spare parts aligned with the active PM rack footprint.',
    pmSummary: pm.count ? `${plural(pm.count, 'work order')} across ${plural(serviceDays, 'service day')}.` : '',
    cmSummary: hasCm ? `${plural(cm.count, 'incident')}, ${plural(cm.completed, 'closure')} recorded.` : '',
    charts: {
      activityTimeline: buildTimeline(pmRecords, cmRecords),
      faultCategories: series(mostCommon(cmRecords, (r) => categoriseFault(r.fault_description), 6)),
      rackCoverage: series(mostCommon(pmRecords, (r) => extractRack(r.activity_code), 5)),
      correctiveActions: series(actions.slice(0, 5)),
    },
  };
}
